import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { WalletLink } from "./WalletLink";
import { cardsPerThousandPoints, formatCards } from "../utils/format";
import { Skeleton } from "./Skeleton";

interface AirdropRecipientsTableProps {
  airdropId: string;
  /** Show the distributed / recipients / points summary tiles above the table */
  showSummary?: boolean;
}

const LIMITS = [50, 100, 250, 500];

export function AirdropRecipientsTable({
  airdropId,
  showSummary = true,
}: AirdropRecipientsTableProps) {
  const [limit, setLimit] = useState(100);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["airdrop-recipients", airdropId, limit],
    queryFn: async () => {
      const { data, error } = await api
        .airdrops({ airdropId })
        .recipients.get({ query: { limit } });
      if (error) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return <Skeleton kind="table" rows={8} />;
  }

  if (error) {
    return (
      <p className="text-red-400">Could not load recipients: {String(error)}</p>
    );
  }

  if (!data) return null;

  const { airdrop } = data;
  const avgCardsPer1k = cardsPerThousandPoints(
    airdrop.totalDistributed,
    data.totalPlatformGain
  );

  return (
    <div className="space-y-4">
      {showSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">$CARDS distributed</div>
            <div className="text-xl font-semibold text-yellow-400 num">
              {formatCards(airdrop.totalDistributed)}
            </div>
          </div>
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Recipients</div>
            <div className="text-xl font-semibold text-gray-50 num">
              {airdrop.recipientCount.toLocaleString()}
            </div>
          </div>
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Platform points gained</div>
            <div className="text-xl font-semibold text-gray-50 num">
              {data.totalPlatformGain.toLocaleString()}
            </div>
          </div>
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Avg $CARDS per 1k points</div>
            <div className="text-xl font-semibold text-gray-50 num">
              {avgCardsPer1k !== null ? formatCards(avgCardsPer1k) : "—"}
            </div>
          </div>
        </div>
      )}

      {data.data.length === 0 ? (
        <p className="text-gray-500 py-4">
          No claims indexed for this airdrop yet. Recipients appear here once
          the airdrop's source wallets are configured and the indexer has run.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm">Show</span>
            <div className="seg">
              {LIMITS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLimit(l)}
                  className={`seg-btn ${limit === l ? "seg-btn-on" : ""}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <span className="text-gray-500 text-xs ml-auto num">
              of {airdrop.recipientCount.toLocaleString()} recipients
            </span>
          </div>

          <div className={`overflow-x-auto ${isFetching ? "opacity-60" : ""}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="pb-3 pr-4 w-16 font-medium">Rank</th>
                  <th className="pb-3 pr-4 font-medium">Wallet</th>
                  <th className="pb-3 pr-4 text-right font-medium">$CARDS received</th>
                  <th className="pb-3 pr-4 text-right font-medium">Points gained</th>
                  <th className="pb-3 pr-4 text-right font-medium">Points rank</th>
                  <th className="pb-3 pr-4 text-right font-medium">Share</th>
                  <th className="pb-3 text-right font-medium">$CARDS / 1k pts</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => {
                  const ratio = cardsPerThousandPoints(
                    row.amountReceived,
                    row.pointsGained
                  );
                  return (
                    <tr key={row.walletAddress} className="table-row">
                      <td className="py-3 pr-4 text-gray-400 font-medium num">
                        #{row.amountRank}
                      </td>
                      <td className="py-3 pr-4">
                        <WalletLink address={row.walletAddress} />
                      </td>
                      <td className="py-3 pr-4 text-right font-semibold text-yellow-400 num">
                        {formatCards(row.amountReceived)}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-100 num">
                        {row.pointsGained !== null
                          ? row.pointsGained.toLocaleString()
                          : "—"}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300 num">
                        {row.pointsRank !== null ? `#${row.pointsRank}` : "—"}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300 num">
                        {row.sharePercent !== null
                          ? `${row.sharePercent.toFixed(2)}%`
                          : "—"}
                      </td>
                      <td className="py-3 text-right text-gray-300 num">
                        {ratio !== null ? formatCards(ratio) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
